import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  BarChart,
  Button,
  DEFAULT_CHART_COLORS,
  DownloadIcon,
  formatData,
  HelpIcon,
  Layout,
  OverlayTriggerTooltip,
  Select,
  Tabs,
  tooltipFormatter,
} from '@utilities';

import { QUESTION_TYPES } from '@api/instant_survey';

import ModalDownloadBestSets from '../../../../../../../../components/ModalDownloadBestSets';

import { DISPLAY_AS } from '../../utilities/helpers';

import ConceptImageList from '../ConceptImageList';
import ModalTop2Box from '../ModalTop2Box';
import OptionImageTable from '../OptionImageTable';
import OtherPleaseSpecifyTable from '../OtherPleaseSpecifyTable';

import { buildSortedBarChart, normalizeAnswerOptionChart } from './utilities/helpers';
import styles from './_index.module.scss';

const ComparisonCharts = ({
  compareByGroups,
  compareByValue = 'overall',
  conceptImages,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  exportQuery,
  globalFilters,
  hideChart = false,
  isAnswerOptionFilteredChart = false,
  isAskWhySurvey = false,
  onOpenConceptImage,
  onOpenLightbox,
  onTop2BoxApplied,
  question,
  questionOptions,
  renderKeyFindings,
  selectedAnswerOptions = [],
  sortByValue = 'default',
  sortOrder = 'default',
  survey,
}) => {
  const [isTop2BoxModalActive, setIsTop2BoxModalActive] = useState(false);
  const [isTurfAnalysis, setIsTurfAnalysis] = useState();
  const [selectedSegmentId, setSelectedSegmentId] = useState();
  const [isDownloadBestSetsModalOpen, setIsDownloadBestSetsModalOpen] = useState(false);

  const turfData = data?.turfAnalysis;

  const canTop2Box = !isAskWhySurvey && survey && question?.type === QUESTION_TYPES.RADIO.value;

  useEffect(() => {
    setIsTurfAnalysis((prev) => {
      if (turfData) return prev ?? true;
      return false;
    });
    if (!turfData) setSelectedSegmentId(undefined);
  }, [turfData]);

  const analysisOptions = [
    { label: 'TURF', value: true },
    { label: 'Standard', value: false },
  ];
  const selectedAnalysisOption =
    analysisOptions.find((option) => option.value === isTurfAnalysis) ?? analysisOptions[0];

  const chartToDisplay = displayAs === DISPLAY_AS.PERCENT.value ? data?.chartPct : data?.chartCount;
  const tableToDisplay = displayAs === DISPLAY_AS.PERCENT.value ? data?.tablePct : data?.tableCount;

  const selectedSegment = useMemo(
    () => turfData?.segments?.find(({ id }) => id === selectedSegmentId),
    [turfData?.segments, selectedSegmentId]
  );

  const activeChart = isTurfAnalysis ? selectedSegment?.chart : chartToDisplay;
  const activeTable = isTurfAnalysis ? selectedSegment?.table : tableToDisplay;
  const normalizedActiveChart = useMemo(
    () => normalizeAnswerOptionChart({ activeChart, selectedAnswerOptions }),
    [activeChart, selectedAnswerOptions]
  );

  const colors = useMemo(() => {
    const series = normalizedActiveChart?.series;
    if (!compareByGroups?.length || !series?.length) return DEFAULT_CHART_COLORS;

    let unusedColorIndex = 0;
    const unusedColors = DEFAULT_CHART_COLORS;

    return series.map(({ id: seriesId, name: seriesName }) => {
      const matchingGroup = compareByGroups?.find(
        ({ id: groupId, name: groupName }) => groupId === seriesId || groupName === seriesName
      );
      return matchingGroup?.color || unusedColors[unusedColorIndex++ % unusedColors.length];
    });
  }, [compareByGroups, normalizedActiveChart?.series]);

  const isOverall = compareByValue === 'overall';
  const hasSortBySelection = sortByValue !== 'default';

  const hasNumericXPoints = useMemo(() => {
    const series = normalizedActiveChart?.series;
    if (!Array.isArray(series)) return false;

    return series.some((s) => {
      const dataArr = Array.isArray(s?.data) ? s.data : [];
      return dataArr.some((pt) => {
        if (Array.isArray(pt)) return typeof pt[0] === 'number'; // [x, y]
        return pt && typeof pt === 'object' && typeof pt.x === 'number'; // { x, y }
      });
    });
  }, [normalizedActiveChart?.series]);

  const canApplySorting = isOverall ? true : hasSortBySelection;
  const effectiveSortOrder = canApplySorting && !hasNumericXPoints ? sortOrder : 'default';

  const { chartPlotOptions, chartSeries, chartXAxis } = useMemo(
    () =>
      buildSortedBarChart({
        activeChart: normalizedActiveChart,
        compareByGroups,
        sortByValue,
        sortOrder: effectiveSortOrder,
      }),
    [compareByGroups, effectiveSortOrder, normalizedActiveChart, sortByValue]
  );
  const hasRenderableNormalizedSeries = chartSeries.some(
    (series) => Array.isArray(series?.data) && series.data.length > 0
  );
  const shouldRenderChart = isAnswerOptionFilteredChart
    ? hasRenderableNormalizedSeries && !hideChart
    : Boolean(activeChart) && !hideChart; // standard charts render whenever chart data is present
  const shouldShowAnswerOptionEmptyState =
    isAnswerOptionFilteredChart &&
    !hideChart &&
    !selectedAnswerOptions.length &&
    !hasRenderableNormalizedSeries;

  useEffect(() => {
    if (turfData?.segments?.length && !selectedSegmentId) {
      setSelectedSegmentId(turfData.segments[0].id);
    }
  }, [turfData?.segments, selectedSegmentId]);

  const questionImage =
    question?.showConceptImage || conceptImages?.length > 0 ? null : question?.image;
  const shouldShowConceptImages = conceptImages?.length > 0 && onOpenConceptImage !== undefined;

  const top2BoxButton = [
    <OverlayTriggerTooltip aria-label="Top2Box" content="Top 2 Box" key="top-2-box-btn">
      <Button
        onClick={() => {
          setIsTop2BoxModalActive(true);
        }}
        text="Top 2 Box"
        variant="secondary"
      />
    </OverlayTriggerTooltip>,
  ];

  return (
    <Layout.Flex.Column className={styles['comparison-charts']} gap="x-large">
      {renderKeyFindings && renderKeyFindings({ question })}
      {shouldShowConceptImages && (
        <ConceptImageList items={conceptImages} onOpen={onOpenConceptImage} />
      )}
      {questionImage?.cdnUrl && onOpenLightbox && (
        <button
          aria-label={`Open image for ${question.title || question.text || 'question'}`}
          className={styles['comparison-charts-question-image-button']}
          onClick={() => onOpenLightbox(question.id)}
          type="button"
        >
          <img
            alt={question.title || question.text || 'Question image'}
            className={styles['comparison-charts-question-image']}
            src={questionImage.cdnUrl}
          />
        </button>
      )}
      {turfData && (
        <div className={styles['comparison-charts-analysis']}>
          <div className={styles['comparison-charts-analysis-container']}>
            <label className={styles['comparison-charts-analysis-select-label']}>Analysis</label>
            <OverlayTriggerTooltip aria-label="Help" content="Help">
              <a
                data-testid="help-link-button"
                href="https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf"
                rel="noreferrer"
                target="_blank"
              >
                <HelpIcon />
              </a>
            </OverlayTriggerTooltip>
          </div>
          <div className={styles['comparison-charts-analysis-actions']}>
            <Select
              className={styles['comparison-charts-analysis-select']}
              onChange={(opt) => setIsTurfAnalysis(Boolean(opt?.value))}
              options={analysisOptions}
              value={selectedAnalysisOption}
            />
            <Button
              icon={<DownloadIcon />}
              onClick={() => setIsDownloadBestSetsModalOpen(true)}
              text="Download Best Sets"
              variant="secondary"
            />
            <ModalDownloadBestSets
              exportQuery={exportQuery}
              globalFilters={globalFilters}
              hasCloseIcon
              isActive={isDownloadBestSetsModalOpen}
              onClose={() => setIsDownloadBestSetsModalOpen(false)}
              survey={survey}
              surveyQuestions={[question]}
            />
          </div>
          <hr />
        </div>
      )}

      {isTurfAnalysis && turfData?.segments?.length > 1 && (
        <Tabs
          onChange={setSelectedSegmentId}
          options={turfData.segments.map(({ id, name }) => ({ label: name, value: id }))}
          value={selectedSegmentId}
        />
      )}

      {shouldShowAnswerOptionEmptyState && (
        <div
          className={styles['comparison-charts-empty-message']}
          data-testid="answer-options-chart-empty-message"
        >
          Select at least one answer option to view the chart.
        </div>
      )}

      {shouldRenderChart && (
        <BarChart
          chartTitle={
            isTurfAnalysis
              ? `Cumulative Reach${selectedSegment?.name ? ` – ${selectedSegment.name}` : ''}`
              : `Answer Options by ${
                  displayAs === DISPLAY_AS.PERCENT.value
                    ? DISPLAY_AS.PERCENT.label
                    : DISPLAY_AS.COUNT.label
                }`
          }
          colors={colors}
          exportName={`${question?.title || 'chart'}${
            isTurfAnalysis
              ? ` - TURF${selectedSegment?.name ? ` - ${selectedSegment.name}` : ''}`
              : ''
          }`}
          key={`${isTurfAnalysis ? 'turf' : 'std'}-${selectedSegmentId ?? 'all'}-${displayAs}`}
          plotOptions={chartPlotOptions}
          series={chartSeries}
          tooltip={{
            formatter: function () {
              const context = this;
              const tooltipData =
                sortOrder && sortOrder !== 'default' && context.point?.name
                  ? {
                      ...context,
                      category: context.point.name,
                    }
                  : context;
              return tooltipFormatter({
                data: tooltipData,
                format: normalizedActiveChart?.format,
              });
            },
          }}
          xAxis={chartXAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format: normalizedActiveChart?.format, value: this.value });
              },
            },
          }}
        />
      )}

      {shouldRenderChart && activeTable && <hr />}
      {activeTable && (
        <OptionImageTable
          actionButtons={canTop2Box ? top2BoxButton : []}
          columns={activeTable?.columns}
          data={activeTable?.data}
          questionOptions={questionOptions}
        />
      )}

      <ModalTop2Box
        isActive={isTop2BoxModalActive}
        onApplied={onTop2BoxApplied}
        onClose={() => setIsTop2BoxModalActive(false)}
        question={question}
        surveyId={survey?.id}
      />

      {!isTurfAnalysis && data?.tableOther?.data?.length > 0 && (
        <>
          <hr />
          <OtherPleaseSpecifyTable columns={data.tableOther.columns} data={data.tableOther.data} />
        </>
      )}
    </Layout.Flex.Column>
  );
};

ComparisonCharts.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  compareByValue: PropTypes.string,
  conceptImages: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
  exportQuery: PropTypes.object.isRequired,
  globalFilters: PropTypes.object,
  hideChart: PropTypes.bool,
  isAnswerOptionFilteredChart: PropTypes.bool,
  isAskWhySurvey: PropTypes.bool,
  onOpenConceptImage: PropTypes.func,
  onOpenLightbox: PropTypes.func,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object.isRequired,
  questionOptions: PropTypes.arrayOf(PropTypes.object),
  renderKeyFindings: PropTypes.func,
  selectedAnswerOptions: PropTypes.arrayOf(PropTypes.string),
  sortByValue: PropTypes.string,
  sortOrder: PropTypes.oneOf(['default', 'asc', 'desc']),
  survey: PropTypes.object,
};

export default ComparisonCharts;
